/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  СТОР АВТОРИЗАЦИИ — кто вошёл, чем он предъявляется и как это переживает F5
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Setup-стор. Публичная поверхность (её импортируют `main.ts`, гвард роутера,
 * `composables/useAuth.ts` и экран сканирования):
 *
 *   state:   user · accessToken · loading · error · ready
 *   getters: isAuthenticated · displayName
 *   actions: bootstrap · login(email, password) · logout · refresh · reset
 *            setAccessToken · applyTokens
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ДВЕ ВЕЩИ, РАДИ КОТОРЫХ ЭТОТ ФАЙЛ ВЫГЛЯДИТ ИМЕННО ТАК
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **1. `bootstrap()` мемоизирован в один промис.** Гвард ждёт его на *каждой*
 * навигации. Access-токен живёт только в памяти (`lib/token-storage.ts`),
 * поэтому на холодной загрузке восстановление сессии — это сетевой round-trip,
 * и две быстрые навигации на старте без мемоизации дали бы **два**
 * `POST /auth/refresh`. Сервер ротирует refresh на каждое использование,
 * второй вызов предъявил бы уже погашенный токен — и живая 30-дневная сессия
 * умерла бы на ровном месте.
 *
 * **2. `refresh()` делегирует в `refreshOnce()` из `lib/http.ts`, а не зовёт
 * `authApi.refresh()`.** Single-flight там общий на всё приложение: ротация,
 * запущенная гвардом, и ротация, запущенная 401-й из зависшего запроса
 * `ResolveParticipantQr`, обязаны схлопнуться в **один** запрос. Своя копия
 * вызова рядом с очередью ровно эту гарантию и ломает.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ЧЕГО ЗДЕСЬ НЕТ — И ПОЧЕМУ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * • **`rememberMe`.** Логин всегда уходит с `rememberMe: true`, стойка всегда
 *   пишет refresh в `localStorage` — см. большой комментарий в
 *   `lib/token-storage.ts`. Регистратор, которого выкинуло на форму входа
 *   посреди очереди, — тот отказ, ради которого это решение и принято.
 *
 * • **`permissions`, `role`, `can()`.** `ResolveParticipantQr` разрешён
 *   `anyRole`, так что и админ, и наблюдатель одинаково пригодны для стойки.
 *   Ролевого гейта в приложении нет вообще; авторитет — сервер, он вернёт 403
 *   независимо от того, что решил фронт. Заводить здесь список прав значило бы
 *   дублировать серверную таблицу ради экрана, который её не спрашивает.
 *
 * • **`resetPeerStores()`.** В админке `reset()` обходил реестр пинии и гасил
 *   соседние сторы, чтобы пользователь B не увидел данные пользователя A.
 *   Здесь соседних сторов нет: экран один, его состояние живёт в компоненте и
 *   умирает вместе с ним при уходе на `/login`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ МОСТ «ТРАНСПОРТ ↔ СТОР» СОБИРАЕТСЯ ЗДЕСЬ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `lib/http.ts` не имеет права импортировать стор (цикл: стор → api → http →
 * стор), поэтому связь односторонняя и инъектируется отсюда, из setup.
 * `main.ts` инстанцирует стор явно между пинией и роутером — именно затем,
 * чтобы момент сборки моста не зависел от того, какой гвард сработал первым.
 *
 * Без моста приложение ломается ровно в том сценарии, ради которого он и
 * нужен: refresh отдал 401 → `lib/http.ts` вычистил хранилище → но стор об
 * этом не узнал, `isAuthenticated` остался `true`, гвард пускает на экран
 * сканирования, и регистратор водит камерой по бейджам, а каждый запрос молча
 * падает.
 */

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { authApi } from '@/api/auth'
import type { ApiError } from '@/lib/api-error'
import { normalizeError } from '@/lib/api-error'
import { refreshOnce, setAuthLostHandler, setTokensRotatedHandler } from '@/lib/http'
import { tokenStorage } from '@/lib/token-storage'
import type { TokenPair, User } from '@/types/api'

/**
 * Транспортная неудача, а не авторитетный отказ: сервер не ответил (0) или
 * ответил 5xx. Отличается от 4xx тем, что refresh-токен при ней **остаётся**
 * на диске (`lib/http.ts`, `isSessionLost`), то есть сессию ещё можно спасти —
 * и значит `bootstrap()` имеет право попробовать ещё раз на следующей навигации.
 */
function isTransient(error: ApiError): boolean {
  return error.status === 0 || error.status >= 500
}

export const useAuthStore = defineStore('auth', () => {
  /* ═══════════════════════════ state ═══════════════════════════ */

  /** Профиль оператора стойки. `null` — не вошли. */
  const user = ref<User | null>(null)

  /**
   * **Зеркало** access-токена для реактивности: настоящее хранилище —
   * модульная переменная в `lib/token-storage.ts`, и на диск он не ложится
   * никогда. Отсюда же и необходимость `bootstrap()`: на холодной загрузке
   * здесь всегда `null`, даже при совершенно живой сессии.
   */
  const accessToken = ref<string | null>(null)

  /** Форма входа в полёте — для `disabled` и спиннера на кнопке. */
  const loading = ref(false)

  /**
   * Последняя ошибка авторизации — для диагностики и для экранов, которым
   * нужен её `reason`. `shallowRef`: `ApiError` — не реактивная структура,
   * оборачивать её поля в прокси незачем.
   *
   * ⚠️ Форма входа **не** читает это поле: она показывает собственный текст,
   * разложенный по полям (`LoginView.vue`). Здесь лежит именно «что случилось
   * с сессией в последний раз», а не «что показать пользователю».
   */
  const error = shallowRef<ApiError | null>(null)

  /** `bootstrap()` отработал — успешно или нет. */
  const ready = ref(false)

  /* ═══════════════════════════ getters ═════════════════════════ */

  const isAuthenticated = computed(() => accessToken.value !== null && user.value !== null)

  /** Как подписать оператора на экране сканирования. Пустая строка, если не вошли. */
  const displayName = computed(() => {
    const u = user.value
    if (!u) return ''
    return u.fullName || u.username || u.email
  })

  /* ═══════════════════════════ внутреннее ══════════════════════ */

  /**
   * Мемоизация `bootstrap()`. **Не сбрасывается после успеха**: восстанавливать
   * сессию во второй раз за жизнь вкладки незачем — дальше её поддерживает
   * `refreshOnce()` из интерцептора. Сбрасывается только при транспортной
   * неудаче, чтобы «Wi-Fi моргнул на две секунды» не означало «до перезагрузки
   * страницы вы аноним».
   */
  let bootstrapPromise: Promise<void> | null = null

  /* ═══════════════════════════ actions ═════════════════════════ */

  /** Кладёт пару токенов и в хранилище, и в зеркало. */
  function applyTokens(tokens: TokenPair): void {
    tokenStorage.save(tokens)
    accessToken.value = tokens.accessToken
  }

  /**
   * Только зеркало, без записи на диск.
   *
   * Существует ради моста с транспортом: к моменту `onTokensRotated`
   * `lib/http.ts` уже вызвал `tokenStorage.save()`, и вторая запись была бы
   * лишней. Для «сохранить новую пару» есть `applyTokens()`.
   */
  function setAccessToken(token: string | null): void {
    accessToken.value = token
  }

  /**
   * Зачистка **только оперативной памяти**: стор и зеркало access-токена.
   * Refresh-токен на диске при этом не трогается.
   *
   * Нужна отдельно от `reset()` ровно для одного случая — транспортного сбоя в
   * `bootstrap()`. Сервер, который не ответил или ответил 502, ничего не сказал
   * о действительности refresh-токена: он не был отвергнут, он не был даже
   * рассмотрен. Стереть его в этот момент — значит превратить двухсекундный
   * провал связи в разлогин 30-дневной сессии, причём необратимый: «повторить
   * попытку» уже нечем.
   */
  function resetState(): void {
    user.value = null
    accessToken.value = null
    loading.value = false
  }

  /**
   * Полная зачистка: выход, потеря сессии, `clear()` из соседней вкладки.
   *
   * Идемпотентна и **никогда не бросает** — её зовут из хендлеров транспорта,
   * где исключение подменило бы собой настоящую причину провала.
   */
  function reset(): void {
    resetState()
    tokenStorage.clear()
  }

  /**
   * Ротация токена силами общего single-flight.
   *
   * ⚠️ Именно `refreshOnce()`, а не `authApi.refresh()` — см. шапку файла.
   * Зеркало обновляем и здесь: хендлер `onTokensRotated` сделает то же самое,
   * но `refresh()` обязан работать и без собранного моста (тесты, заглушки).
   */
  async function refresh(): Promise<void> {
    const token = await refreshOnce()
    accessToken.value = token
  }

  /**
   * Вход.
   *
   * `rememberMe: true` — **всегда**, галочки на форме нет: сервер выдаёт
   * 30-дневную refresh-сессию, клиент кладёт токен в `localStorage`. Обоснование
   * — в шапке `lib/token-storage.ts`.
   *
   * Ошибку **пробрасываем наверх** — её разбирает `LoginView`:
   * `INVALID_CREDENTIALS` рисуется под полем пароля, `RATE_LIMITED` и
   * `USER_DISABLED` — над формой. Стор не знает, где у формы поля.
   *
   * Параметр называется `email`, потому что так подписано поле на экране, но
   * сервер принимает и username: это одно поле `login`, и он сам разбирает.
   */
  async function login(email: string, password: string): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await authApi.login({ login: email, password, rememberMe: true })

      const tokens = response.tokens
      if (!tokens) {
        // Контракт такого не допускает, тип (`TokenPair | null`) — допускает.
        throw new Error('login: ответ без пары токенов')
      }

      // Порядок значим: токен должен лечь в хранилище ДО любого следующего
      // запроса, иначе тот уйдёт без заголовка `Authorization` и получит 401.
      applyTokens(tokens)

      /**
       * `LoginResponse.user` типизирован `User | null`, и `isAuthenticated`
       * требует пользователя: без него гвард увидит анонима и вернёт человека
       * на форму, с которой тот только что успешно вошёл. Поэтому `me()` —
       * страховка, а не обычный путь: один лишний round-trip случается только
       * тогда, когда логин почему-то не привёз профиль.
       *
       * (В админке `me()` звался всегда — ради `permissions`. Здесь прав нет,
       *  и на зальном Wi-Fi лишний запрос на каждом входе не бесплатен.)
       */
      const profile = response.user ?? (await authApi.me()).user
      if (!profile) throw new Error('login: ответ без пользователя')
      user.value = profile

      // Сессия восстановлена явным входом — ближайшему проходу гварда
      // бутстрапить нечего.
      bootstrapPromise = Promise.resolve()
      ready.value = true
    } catch (err) {
      const normalized = normalizeError(err)
      error.value = normalized
      throw normalized
    } finally {
      loading.value = false
    }
  }

  /**
   * Выход. Провал серверного вызова **проглатывается**: человек нажал «Выйти»,
   * и запереть его внутри приложения из-за отвалившегося Wi-Fi нельзя. Локально
   * мы вышли в любом случае — отсюда `reset()` вне зависимости от исхода.
   *
   * `skipAuth` на самом запросе стоит в `api/auth.ts`: выход обязан срабатывать
   * и с протухшим access-токеном.
   */
  async function logout(): Promise<void> {
    const refreshToken = tokenStorage.getRefreshToken()
    try {
      if (refreshToken) await authApi.logout({ refreshToken })
    } catch {
      /* сервер не подтвердил выход — локально мы всё равно вышли */
    } finally {
      error.value = null
      reset()
    }
  }

  /**
   * Восстановление сессии на холодной загрузке. **Мемоизирован** — см. шапку.
   *
   * Контракт:
   *   • нет refresh-токена → мы аноним, ни одного запроса не делаем;
   *   • иначе `refresh()` (общий single-flight), профиль приезжает вместе с
   *     новой парой токенов через мост; не приехал — добираем `authApi.me()`;
   *   • любая неудача → чистим стор и продолжаем анонимом;
   *   • всегда заканчивается `ready = true`;
   *   • **никогда не бросает** — гвард оборачивает вызов в `try/catch`, но
   *     нуждаться в этом он не должен.
   */
  async function bootstrap(): Promise<void> {
    bootstrapPromise ??= runBootstrap()
    return bootstrapPromise
  }

  async function runBootstrap(): Promise<void> {
    try {
      if (!tokenStorage.getRefreshToken()) {
        // Штатный путь холодного старта не вошедшего пользователя, а не сбой:
        // предъявлять просто нечего.
        reset()
        return
      }

      await refresh()

      /**
       * `RefreshResponse.user` перечитан из БД и обычно уже разложен по стору
       * хендлером `onTokensRotated` (см. `wireBridge`). Запрос делается только
       * если профиля всё-таки нет: на старте приложения экономия одного
       * round-trip'а — это время до первого кадра камеры.
       */
      if (!user.value) {
        const me = await authApi.me()
        user.value = me.user
      }

      error.value = null
    } catch (err) {
      const normalized = normalizeError(err)
      error.value = normalized

      if (isTransient(normalized)) {
        /*
         * Сеть/5xx: refresh-токен цел и остаётся на диске — `performRefresh()`
         * стирает его только на авторитетном 4xx. Поэтому здесь **`resetState()`,
         * а не `reset()`**: сбрасываем стор, но не хранилище.
         *
         * Это не микрооптимизация. `reset()` тут стирал бы ровно тот токен,
         * ради которого следующая попытка и затевается: оператор с живой
         * сессией, открывший стойку в секунду перезапуска бэкенда, оказывался бы
         * разлогинен окончательно — «попробовать ещё раз» было бы уже нечем.
         *
         * Мемо снимаем, чтобы следующая навигация попробовала снова.
         */
        resetState()
        bootstrapPromise = null
      } else {
        // Авторитетный отказ (4xx): токен уже мёртв, хранилище чистит `reset()`
        // — даже если `performRefresh()` до этого не дошёл (например, упал
        // `GET /auth/me`).
        reset()
      }
    } finally {
      ready.value = true
    }
  }

  /* ═══════════════════════ мост с транспортом ══════════════════ */

  /**
   * Идемпотентна: повторный вызов просто перезапишет сеттеры теми же
   * функциями. См. шапку файла о том, почему мост живёт здесь.
   */
  function wireBridge(): void {
    // Сессия потеряна безвозвратно: refresh отдал 4xx, токены уже стёрты.
    setAuthLostHandler((lost) => {
      error.value = lost
      reset()
      void redirectToLogin()
    })

    // Токены отротировались. `user` приезжает вместе с ними и **перечитан из
    // БД**: если учётку отключили, здесь это уже видно, тогда как claim в
    // старом access-токене врал бы до конца своего TTL.
    setTokensRotatedHandler((tokens, rotatedUser) => {
      setAccessToken(tokens.accessToken)
      if (rotatedUser) user.value = rotatedUser
    })

    // Соседняя вкладка вычистила storage (выход или отзыв) — эта тоже выходит.
    tokenStorage.onExternalClear(() => {
      reset()
      void redirectToLogin({ preserveTarget: false })
    })
  }

  /**
   * Уход на форму входа после потери сессии.
   *
   * Роутер импортируется **динамически**: `router/index.ts` → `guards.ts` →
   * этот файл, то есть статический импорт замкнул бы цикл. К моменту вызова
   * модуль давно загружен, так что это просто разрешённый промис из кеша.
   *
   * `preserveTarget: false` для кросс-вкладочного выхода: там сессию погасили
   * намеренно, и возвращать человека ровно туда, откуда его выкинули, — не то,
   * о чём он просил.
   */
  async function redirectToLogin(options: { preserveTarget?: boolean } = {}): Promise<void> {
    const { preserveTarget = true } = options
    try {
      const { default: router } = await import('@/router')
      const current = router.currentRoute.value
      if (current.name === 'login') return

      const query = preserveTarget && current.fullPath !== '/'
        ? { redirect: current.fullPath }
        : undefined

      await router.replace({ name: 'login', query })
    } catch {
      /* навигация отменена другим гвардом — не наша забота */
    }
  }

  wireBridge()

  return {
    // state
    user,
    accessToken,
    loading,
    error,
    ready,
    // getters
    isAuthenticated,
    displayName,
    // actions
    bootstrap,
    login,
    logout,
    refresh,
    reset,
    setAccessToken,
    applyTokens,
  }
})
